/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Vang Pham (ptvang@rsupport.com)
 *
 * @description
 *
 * @created at Fri Aug 9 2024
 **/

import styled from "@emotion/styled";
import { CircularProgress, css } from "@mui/material";

import { _T } from "@rfice/language";
import { Row, Typography, Button } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries";

type UnsupportPreviewProps = {
	onClick: () => void;
	isDownloading: boolean;
	isMyDesk?: boolean;
	isChat?: boolean;
};

export default function UnsupportPreview({
	onClick,
	isDownloading,
	isMyDesk = false,
	isChat = false
}: UnsupportPreviewProps) {
	const downloadFile = (ev: React.MouseEvent<HTMLButtonElement>) => {
		ev.stopPropagation();
		onClick();
	};

	return (
		<DownloadWrapper className="download-wrapper" isDownloading={isDownloading} isMyDesk={isMyDesk} isChat={isChat}>
			<Maybe test={isDownloading}>
				<Row css={{ flexDirection: "column" }} verticalAlign="center">
					<Typography> {isMyDesk ? _T("다운로드 중") : _T("파일이 다운로드 됩니다.")}</Typography>
					<CircularProgress size={22} color="inherit" />
				</Row>
				<Maybe.Fallback>
					<Button.Icon
						name="Download24"
						size="XXlarge"
						filled={isMyDesk || isChat ? "neutral" : "transparent"}
						fill={isMyDesk || isChat ? "neutral_00_68" : "neutral_80_100"}
						css={{ margin: "0 auto" }}
						onClick={downloadFile}
					/>
				</Maybe.Fallback>
			</Maybe>
		</DownloadWrapper>
	);
}

const DownloadWrapper = styled(Row)<{ isDownloading: boolean; isMyDesk: boolean; isChat: boolean }>`
	position: absolute;
	top: 0;
	bottom: 0;
	right: 0;
	${({ isMyDesk, isChat }) => {
		if (isMyDesk) {
			return css`
				width: 128px;
				border-radius: 0px 12px 12px 0px;
				background: linear-gradient(270deg, #787878 0%, #787878 50%, rgba(120, 120, 120, 0) 100%);
				align-content: center;
			`;
		} else if (isChat) {
			return css`
				border-radius: 0 4px 4px 0;
				width: 128px;
				height: 50px;
				flex-shrink: 0;
				background: linear-gradient(270deg, #dcdcdc 0%, #dcdcdc 50%, rgba(220, 220, 220, 0) 100%);
			`;
		} else {
			return css`
				background: #d9d9d9;
				left: 0;
			`;
		}
	}};
	padding: ${({ isChat }) => (isChat ? "0 0" : `6px 10px`)};
	display: ${({ isDownloading }) => {
		return isDownloading ? "block" : "none";
	}};
	cursor: default;
	* {
		cursor: default;
	}

	& {
		button svg,
		button path {
			cursor: pointer;
		}

		button {
			${({ isChat }) => {
				if (isChat) {
					return `
						width: 36px;
						height: 36px;
						flex-shrink: 0;
						position: absolute;
						right: 16px;
						top: 7px;
						bottom: 7px;
				`;
				}
			}}
		}
	}
`;
